import React, { useEffect, useState } from "react";
import { Form, Input, Select, message } from "antd";
import { Button } from "evergreen-ui";
import { MDBCol } from "mdbreact";
import { useListVals } from "../../Firebase/databaseHooks";
import Firebase from "../../Firebase/Firebase";
import { getDatabase, ref } from "firebase/database";
import { useAuth } from "../../AuthContext/AuthContext";
import { USER_STATUS } from "../../../constants/userStatus";
import { getCallable } from "../../Firebase/functionsClient";
import { filterSelectOption, sortSelectOptions } from "../../utils/selectOptions.js";

const db = getDatabase(Firebase);

const createUserFn = getCallable("createUser");
const listUserRolesFn = getCallable("listUserRoles");
const listVolunteerTypesFn = getCallable("listVolunteerTypes");

const AddUser = (props) => {
	const { canAccessModule, firebaseUser, loading: authLoading } = useAuth();
	const canCreateUser = canAccessModule("USERS", "create");
	const [form] = Form.useForm();
	const [showLoading, setShowLoading] = useState(false);
	const [arrayUsers] = useListVals(ref(db, "talmidsdb/users"));
	const [countryArray] = useListVals(ref(db, "talmidsdb/countries"));
	const [languageArray] = useListVals(ref(db, "talmidsdb/languages"));
	const [communityArray] = useListVals(ref(db, "talmidsdb/communities"));
	const [orgArray] = useListVals(ref(db, "talmidsdb/organisations"));
	const [volunteerArray, setVolunteerArray] = useState([]);
	const [volunteerLoading, setVolunteerLoading] = useState(false);
	const [volunteerError, setVolunteerError] = useState(null);
	const [roleArray, setRoleArray] = useState([]);
	const [rolesLoading, setRolesLoading] = useState(false);
	const [rolesError, setRolesError] = useState(null);
	const [isComVol, setIsComVol] = useState(false);

	const [selectedCountries, setSelectedCountries] = useState([]);
	const [selectedOrgs, setSelectedOrgs] = useState([]);

	const resetFormAndState = () => {
		form.resetFields();
		setSelectedCountries([]);
		setSelectedOrgs([]);
		setIsComVol(false);
	};

	useEffect(() => {
		let isMounted = true;

		const loadUserRoles = async () => {
			if (authLoading) return;

			if (!firebaseUser) {
				if (isMounted) {
					setRoleArray([]);
					setRolesError(new Error("Not authenticated"));
					setRolesLoading(false);
				}
				return;
			}

			setRolesLoading(true);
			setRolesError(null);

			try {
				const res = await listUserRolesFn({});
				const roles = Array.isArray(res?.data?.roles) ? res.data.roles : [];
				if (isMounted) {
					setRoleArray(roles);
				}
			} catch (error) {
				if (isMounted) {
					setRoleArray([]);
					setRolesError(error);
				}
			} finally {
				if (isMounted) {
					setRolesLoading(false);
				}
			}
		};

		loadUserRoles();

		return () => {
			isMounted = false;
		};
	}, [authLoading, firebaseUser]);

	useEffect(() => {
		let isMounted = true;

		const loadVolunteerTypes = async () => {
			if (authLoading) return;

			if (!firebaseUser) {
				if (isMounted) {
					setVolunteerArray([]);
					setVolunteerError(new Error("Not authenticated"));
					setVolunteerLoading(false);
				}
				return;
			}

			setVolunteerLoading(true);
			setVolunteerError(null);

			try {
				const res = await listVolunteerTypesFn({});
				const types = Array.isArray(res?.data?.volunteerTypes) ? res.data.volunteerTypes : [];
				if (isMounted) {
					setVolunteerArray(types);
				}
			} catch (error) {
				if (isMounted) {
					setVolunteerArray([]);
					setVolunteerError(error);
				}
			} finally {
				if (isMounted) {
					setVolunteerLoading(false);
				}
			}
		};

		loadVolunteerTypes();

		return () => {
			isMounted = false;
		};
	}, [authLoading, firebaseUser]);

	const addNewUser = async (values) => {
		if (authLoading || !firebaseUser) {
			message.error("Your session is not ready. Please sign in again and retry.");
			return;
		}

		if (!canCreateUser) {
			message.error("You do not have permission to create users.");
			return;
		}

		if (rolesError) {
			message.error("Unable to load user roles. Please refresh and try again.");
			return;
		}

		setShowLoading(true);
		try {
			const normalizedEmail = String(values.email || "").trim().toLowerCase();
			const normalizedPhone = String(values.phone || "").replace(/\s+/g, "");

			if (!normalizedEmail && !normalizedPhone) {
				message.error("Please provide at least a phone number or an email.");
				return;
			}

			const payload = {
				...values,
				email: normalizedEmail,
				phone: normalizedPhone,
			};

			const { data } = await createUserFn(payload);
			const createdUid = data?.uid || "";
			const emailNotificationsAttempted = Boolean(data?.emailNotificationsAttempted);
			const emailNotificationsSent =
				data?.emailNotificationsSent === undefined ? true : Boolean(data.emailNotificationsSent);

			if (!createdUid) {
				throw new Error("No uid returned from createUser");
			}

			message.success(
				!emailNotificationsAttempted || emailNotificationsSent
					? "User added successfully"
					: "User added successfully, but email notifications could not be sent."
			);
			resetFormAndState();
			props.modal(false);
		} catch (err) {
			console.error("Error adding user:", err);
			switch (err?.code) {
				case "functions/already-exists":
					message.error(err?.message || "User already exists.");
					break;
				case "functions/invalid-argument":
					message.error(err?.message || "Invalid user payload.");
					break;
				case "functions/permission-denied":
					message.error("You do not have permission to create users.");
					break;
				case "functions/unauthenticated":
					message.error("Your session expired. Please sign in again.");
					break;
				default:
					message.error("Failed to add user: " + (err?.message || "Unknown error"));
			}
		} finally {
			setShowLoading(false);
		}
	};

	return (
		<MDBCol md={12}>
			<Form layout="vertical" form={form} onFinish={addNewUser}>
				<Form.Item
					label="First name"
					name="firstname"
					rules={[{ required: true, message: "Please input first name!" }]}
				>
					<Input placeholder="enter user firstname" />
				</Form.Item>

				<Form.Item
					label="Surname"
					name="surname"
					rules={[
						{ required: true, message: "Please input surname!" },
						({ getFieldValue }) => ({
							validator(_, value) {
								if (
									arrayUsers?.some(
										(x) =>
											(x.firstname + x.surname).toLowerCase() ===
											(getFieldValue("firstname") + value).toLowerCase()
									)
								) {
									return Promise.reject(
										"A user already exists with the same firstname and surname!"
									);
								}
								return Promise.resolve();
							},
						}),
					]}
				>
					<Input placeholder="enter user surname" />
				</Form.Item>

				<Form.Item
					label="Phone"
					name="phone"
					rules={[
						{ required: true, message: "Please input phone!" },
						{
							validator(_, value) {
								if (!value) return Promise.reject("Please input phone!");

								const raw = value.replace(/\s+/g, "");

								// Must start with +
								if (!raw.startsWith("+")) {
									return Promise.reject(
										"Please enter phone in international format starting with + (e.g. +2609...)."
									);
								}

								// After +, only digits
								if (!/^\+\d+$/.test(raw)) {
									return Promise.reject(
										"Phone number must contain only digits after the + sign."
									);
								}

								if (raw.length < 11 || raw.length > 16) {
									return Promise.reject(
										"Phone number must be between 10 and 15 digits long."
									);
								}

								if (
									arrayUsers?.some(
										(x) =>
											String(x.phone || "")
												.replace(/\s+/g, "")
												.toLowerCase() === raw.toLowerCase()
									)
								) {
									return Promise.reject(
										"This phone number already exists in the system!"
									);
								}

								return Promise.resolve();
							},
						},
					]}
				>
					<Input type="tel" placeholder="e.g. +260977123456" />
				</Form.Item>

				<Form.Item
					label="Email"
					name="email"
					rules={[
						{
							type: "email",
							message: "Please enter a valid email!",
						},
						{
							required: false,
						},
					]}
				>
					<Input type="email" placeholder="enter user email" />
				</Form.Item>

				<Form.Item
					label="Preferred Language"
					name="preferredLanguage"
					rules={[{ required: true, message: "Please select preferred language!" }]}
				>
					<Select
						placeholder="Select preferred language"
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{languageArray?.map((item, index) => (
							<Select.Option key={index} value={item.id}>
								{item.languageName || item.language}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label="Supervisor" name="supervisor">
					<Select
						placeholder="Select user supervisor"
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{arrayUsers?.map((item, index) => (
							<Select.Option key={index} value={item.id}>
								{item.firstname + " " + item.surname}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="Gender"
					name="gender"
					rules={[{ required: true, message: "Please input gender!" }]}
				>
					<Select
						placeholder="Select user gender"
						showSearch
						style={{ width: "100%" }}
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{["Male", "Female", "Rather Not Say"]?.map((item, index) => (
							<Select.Option key={index} value={item}>
								<span style={{ whiteSpace: "nowrap" }}>{item}</span>
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				{/* MULTI-COUNTRY */}
				<Form.Item
					label="Countries"
					name="countries"
					rules={[
						{
							required: true,
							type: "array",
							min: 1,
							message: "Please select at least one country!",
						},
					]}
				>
					<Select
						placeholder="Select user countries"
						mode="multiple"
						showSearch
						onChange={(vals) => setSelectedCountries(vals)}
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{countryArray?.map((item, index) => (
							<Select.Option key={index} value={item.id}>
								{item.countryName}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				{/* MULTI-ORG */}
				<Form.Item label="Organisations" name="organisations">
					<Select
						placeholder="Select user organisations"
						mode="multiple"
						showSearch
						onChange={(vals) => setSelectedOrgs(vals)}
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{orgArray
							?.filter((org) => {
								if (!selectedCountries.length) return true;
								return selectedCountries.includes(org.countryID);
							})
							.map((item, index) => (
								<Select.Option key={index} value={item.id}>
									{item.name}
								</Select.Option>
							))}
					</Select>
				</Form.Item>

				{/* MULTI-COMMUNITY */}
				<Form.Item
					label="Communities"
					name="communities"
					rules={[
						{
							required: true,
							type: "array",
							min: 1,
							message: "Please select at least one community!",
						},
					]}
				>
					<Select
						placeholder="Select user communities"
						mode="multiple"
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{communityArray
							?.filter((community) => {
								const matchesCountry =
									selectedCountries.length &&
									community.countryID &&
									selectedCountries.includes(community.countryID);
								const matchesOrg =
									selectedOrgs.length &&
									community.orgID &&
									selectedOrgs.includes(community.orgID);
								// If no filters, show all; otherwise show matching ones
								if (!selectedCountries.length && !selectedOrgs.length) {
									return true;
								}
								return matchesCountry || matchesOrg;
							})
							.map((item, index) => (
								<Select.Option key={index} value={item.id}>
									{item.name}
								</Select.Option>
							))}
					</Select>
				</Form.Item>

				<Form.Item label="Worker Type" name="volunteerType">
					<Select
						placeholder="Select Worker type"
						mode="multiple"
						loading={volunteerLoading || authLoading}
						disabled={volunteerLoading || authLoading}
						notFoundContent={
							volunteerLoading || authLoading ?
								"Loading worker types..." :
								volunteerError ? "Unable to load worker types" : "No worker types found"
						}
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{volunteerArray?.map((item, index) => (
							<Select.Option key={index} value={item.id}>
								{item.volunteerType}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="User Role"
					name="userRole"
					rules={[{ required: true, message: "Please input user role!" }]}
					validateStatus={rolesError ? "error" : undefined}
					help={rolesError ? "Unable to load user roles for this account." : undefined}
				>
					<Select
						placeholder="Select user role"
						loading={rolesLoading || authLoading}
						disabled={rolesLoading || authLoading}
						notFoundContent={
							rolesLoading || authLoading ? "Loading user roles..." : "No roles found"
						}
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
						onChange={(option) => {
							if (option === "ComVol") {
								setIsComVol(true);
							} else {
								setIsComVol(false);
							}
						}}
					>
						{roleArray?.map((item, index) => (
							<Select.Option key={index} value={item.userRoleID}>
								{item.roleName}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="User Status"
					name="userStatus"
					rules={[{ required: true, message: "Please input user status!" }]}
				>
					<Select
						placeholder="Select user status"
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{[
							USER_STATUS.AWAITING_APPROVAL,
							USER_STATUS.ACTIVE,
							USER_STATUS.NOT_ACTIVE,
							USER_STATUS.SUSPENDED,
							USER_STATUS.PASSWORD_RESET_NEEDED,
						].map((item, index) => (
							<Select.Option key={index} value={item}>
								{item}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				{isComVol && (
					<Form.Item
						label="User Password"
						name="userpassword"
						rules={[{ required: true, message: "Please input password!" }]}
					>
						<Input type="password" placeholder="enter user password" />
					</Form.Item>
				)}

				<div style={{ display: "flex", gap: 8, marginTop: 16 }}>
					<Button
						appearance="warning"
						onClick={() => {
							props.modal(false);
						}}
						disabled={showLoading}
					>
						Close
					</Button>
					<Button
						appearance="primary"
						type="submit"
						isLoading={showLoading}
						intent="success"
						disabled={!canCreateUser}
					>
						Create
					</Button>
				</div>
			</Form>
		</MDBCol>
	);
};

export default AddUser;
